import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FeaturedCourses from '../../../views/LandingPage/EducationDomain/FeaturedCourses.jsx';
import Products from '../../../views/products/index.jsx';
import CourseInformation from '../../../views/products/CourseInfo.jsx';
import Cart from '../../../views/payment/Cart.jsx';
import OrderHistory from '../../../views/payment/OrderHistory.jsx';
import Login from '../../../views/loginOrRegister/Login.jsx';
import Signup from '../../../views/loginOrRegister/Signup.jsx';
import { getAllProductCategories, getAllValidProducts } from '../../../api/action/products.jsx';
import { sanitizeStyleValue } from '../../Sanitizer.jsx';
import { useTemplateContactInfo, useTemplateInquiryForm } from '../../TemplateContactUtils.jsx';
import RichTextDisplay from '../../../components/RichTextDisplay.jsx';
import {
  ButtonLink,
  getBlocks,
  getImageUrl,
  normalizeListResponse,
  toStyle,
} from '../shared/templateSectionUtils.jsx';

const educationPrimaryDefaults = ['#3B9394', '#3b9394', '#0D9488', '#0d9488', '#008060'];
const educationSecondaryDefaults = ['#746EAC', '#746eac', '#7e22ce', '#6b21a8'];
export const educationReaderAssets = {
  course: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=900&q=80&fit=crop&auto=format',
  aboutHero: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/e-learning.png',
  aboutSignup: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/joinUSAbout.png',
  contact: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Education+Domain+Assets/customer-care.jpg',
  curriculum: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/curriculum.png',
  trainer: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/personalTrainer.png',
  classroom: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/classRoom.png',
  building: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/building.png',
  inclusivity: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/inclusivity.png',
  guidance: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/guidance.png',
  eLearningAlt: 'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/elearning.png'
};
const defaultEducationAboutBlocks = [
  { title: 'Cutting-Edge Curriculum', description: 'Immerse your child in an advanced curriculum designed to foster innovation and adaptability in a rapidly changing world.', imageUrl: educationReaderAssets.curriculum, visible: true },
  { title: 'Expert Faculty', description: 'Learn from a team of passionate, experienced educators dedicated to nurturing individual student potential.', imageUrl: educationReaderAssets.trainer, visible: true },
  { title: 'Small Class Sizes', description: 'Enjoy personalized learning experiences in small class settings that prioritize student engagement and interaction.', imageUrl: educationReaderAssets.classroom, visible: true },
  { title: 'State-of-the-Art Facilities', description: 'Discover our cutting-edge learning environments, featuring top-tier technology and resources for modern education.', imageUrl: educationReaderAssets.building, visible: true },
  { title: 'Inclusive and Diverse Community', description: 'Be part of a community that values and celebrates diversity, creating a safe and inclusive space for all students.', imageUrl: educationReaderAssets.inclusivity, visible: true },
  { title: 'Parental Involvement', description: "Partner with us in your child's education, fostering a collaborative relationship that strengthens their academic journey.", imageUrl: educationReaderAssets.guidance, visible: true },
  { title: 'Strong Values-Based Education', description: 'Shape character and leadership through our commitment to teaching strong ethical values and social responsibility.', imageUrl: educationReaderAssets.eLearningAlt, visible: true }
];
const defaultEducationContactFields = [
  { label: 'First Name', type: 'text', visible: true },
  { label: 'Last Name', type: 'text', visible: true },
  { label: 'Email Address', type: 'email', required: true, visible: true },
  { label: 'Phone Number', type: 'tel', visible: true },
  { label: 'Message', type: 'textarea', required: true, visible: true }
];
const themeLinkedColor = (value, themeValue, defaults, fallback) => {
  if (themeValue && (!value || defaults.includes(value))) return themeValue;
  return value || themeValue || fallback;
};
const educationAccent = (section, theme = {}) => themeLinkedColor(section?.style?.accentColor, theme.primary, educationPrimaryDefaults, '#3B9394');
const educationSecondary = (section, theme = {}) => themeLinkedColor(section?.style?.secondaryColor, theme.secondary, educationSecondaryDefaults, '#746EAC');
const educationBlockAccent = (item, section, theme = {}) => {
  const value = item?.accentColor;
  if (theme.primary && educationPrimaryDefaults.includes(value)) return theme.primary;
  if (theme.secondary && educationSecondaryDefaults.includes(value)) return theme.secondary;
  return value || educationAccent(section, theme);
};

const iconKey = (value) => String(value || '').trim().replace(/[\s_-]+/g, '').toLowerCase();

const educationEmojiIcons = {
  book: '\uD83D\uDCDA',
  curriculum: '\uD83D\uDCDA',
  clock: '\u23F0\uFE0F',
  schedule: '\u23F0\uFE0F',
  mentor: '\uD83D\uDC68\u200D\uD83C\uDFEB',
  mentorship: '\uD83D\uDC68\u200D\uD83C\uDFEB',
  career: '\uD83D\uDCBC',
  briefcase: '\uD83D\uDCBC',
  graduate: '\uD83C\uDF93',
  graduation: '\uD83C\uDF93',
  skill: '\uD83C\uDF93',
  faculty: '\uD83C\uDF93',
  idea: '\uD83D\uDCA1',
  insight: '\uD83D\uDCA1',
  innovation: '\uD83D\uDCA1',
  build: '\uD83C\uDFD7\uFE0F',
  project: '\uD83D\uDEE0\uFE0F',
  practical: '\uD83D\uDEE0\uFE0F',
  team: '\uD83E\uDD1D',
  collaboration: '\uD83E\uDD1D',
  guided: '\uD83E\uDDED',
  star: '\u2B50',
  excellence: '\u2B50',
  mission: '\uD83C\uDFAF',
  target: '\uD83C\uDFAF',
  vision: '\uD83D\uDC41\uFE0F',
  facilities: '\uD83C\uDFEB'
};

const EducationIcon = ({ name, fallback, size = 20 }) => (
  <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size, lineHeight: 1 }}>
    {educationEmojiIcons[iconKey(name)] || fallback}
  </span>
);

export const useEducationTemplateData = (template) => {
  const dispatch = useDispatch();
  const companyState = useSelector((state) => state.companyReducer?.company || state.company?.companyDetails);
  const isLoggedIn = useSelector((state) => state.auth?.isAuthenticated) === true;
  const [data, setData] = useState({ products: [], categories: [], company: companyState, isLoggedIn });
  const isEducation = template?.domain?.type === 'EDUCATION' || template?.attributes?.sourceDomain === 'EDUCATION';
  const companyId = companyState?.primaryId;

  useEffect(() => {
    setData((current) => (
      current.isLoggedIn === isLoggedIn && current.company === companyState
        ? current
        : { ...current, company: companyState, isLoggedIn }
    ));
  }, [companyState, isLoggedIn]);

  useEffect(() => {
    let mounted = true;
    if (!isEducation || !companyId) {
      return () => {
        mounted = false;
      };
    }

    const load = async () => {
      try {
        const [productsResponse, categoriesResponse] = await Promise.all([
          dispatch(getAllValidProducts(companyId)),
          dispatch(getAllProductCategories(companyId))
        ]);
        if (mounted) {
          setData((current) => ({
            products: normalizeListResponse(productsResponse),
            categories: normalizeListResponse(categoriesResponse),
            company: companyState,
            isLoggedIn: current.isLoggedIn
          }));
        }
      } catch (error) {
        if (mounted) {
          setData((current) => current);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [companyId, companyState, dispatch, isEducation]);

  return useMemo(
    () => ({ ...data, company: companyState }),
    [companyState, data]
  );
};

const EducationButton = ({ action, section, theme }) => {
  if (!action?.label) return null;
  const accentColor = educationAccent(section, theme);
  const secondaryColor = educationSecondary(section, theme);
  const variant = action.variant || section?.content?.buttonVariant || 'primary';
  const isOutline = variant === 'outline';
  const isGhost = variant === 'ghost';
  const buttonSize = action.size || section?.content?.buttonSize || 'medium';
  const height = buttonSize === 'small' ? 38 : buttonSize === 'large' ? 54 : theme.buttonHeight || 48;
  const buttonColor = action.color || section?.content?.buttonColor || secondaryColor;
  const openNewTab = action.newTab === true || section?.content?.buttonNewTab === true;
  return (
    <a
      href={action.url || '/'}
      target={openNewTab ? '_blank' : undefined}
      rel="noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: height,
        padding: buttonSize === 'small' ? '0 18px' : buttonSize === 'large' ? '0 34px' : '0 28px',
        borderRadius: action.radius || section?.content?.buttonRadius || theme.buttonRadius || 8,
        background: isOutline || isGhost ? 'transparent' : buttonColor,
        border: isOutline ? `2px solid ${accentColor}` : '2px solid transparent',
        color: action.textColor || (isOutline || isGhost ? buttonColor || accentColor : '#ffffff'),
        textDecoration: 'none',
        fontWeight: 700
      }}
    >
      {action.label}
    </a>
  );
};

export const EducationHeroSection = ({ section, theme }) => {
  const content = section.content || {};
  const accentColor = educationAccent(section, theme);
  const secondaryColor = educationSecondary(section, theme);
  const highlights = getBlocks(section, 'highlights');
  const actions = getBlocks(section, 'actions');
  const stats = getBlocks(section, 'stats');
  const highlightBlocks = getBlocks(section, 'highlightBlocks');
  const style = toStyle(section.style || {});
  const advancedBackground = section.style?.backgroundType === 'gradient' || section.style?.backgroundType === 'image' || section.style?.backgroundType === 'none';

  return (
    <section
      className="relative py-20 px-8 bg-cover bg-center min-h-screen flex items-center"
      style={{
        ...style,
        backgroundImage: advancedBackground ? style.backgroundImage : `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.7)), url(${sanitizeStyleValue(section.style?.backgroundImage || content.backgroundImage || '')})`,
        minHeight: section.style?.minHeight || style.minHeight || '100vh'
      }}
    >
      <div className="max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              {content.titlePrefix || 'Transform Your Career with'}{' '}
              <span style={{ color: secondaryColor }}>{content.titleAccent || 'Expert-Led'}</span>{' '}
              {content.titleSuffix || 'Courses'}
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">{content.subtitle}</p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {highlights.map((item, index) => (
                <div className="flex items-center space-x-2" key={`${item.title}-${index}`}>
                  <div className="w-8 h-8 rounded-full" style={{ background: educationBlockAccent(item, section, theme) }} />
                  <span className="text-gray-200">{item.title}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              {actions.map((action, index) => <EducationButton key={`${action.label}-${index}`} action={action} section={section} theme={theme} />)}
            </div>
            <div className="flex space-x-8 text-center">
              {stats.map((item, index) => (
                <div className="text-center" key={`${item.value}-${index}`}>
                  <div className="text-2xl font-bold" style={{ color: secondaryColor }}>{item.value}</div>
                  <div className="text-gray-300 text-sm">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">{content.highlightCardTitle || 'Course Highlights'}</h2>
            <div className="space-y-6">
              {highlightBlocks.map((item, index) => (
                <div className="flex items-start space-x-4" key={`${item.title}-${index}`}>
                  <div className="p-3 rounded-lg" style={{ background: `${educationBlockAccent(item, section, theme)}18`, color: educationBlockAccent(item, section, theme) }}>
                    <EducationIcon name={item.icon} fallback={index + 1} size={22} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <a href={content.highlightButtonUrl || '/course'} className="block w-full text-white py-3 rounded-lg font-semibold text-center" style={{ background: `linear-gradient(90deg, ${secondaryColor}, ${accentColor})` }}>
                {content.highlightButtonLabel || 'Start Learning Journey'}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const EducationCategoryGridSection = ({ section, data, theme }) => {
  const categories = data.categories.length ? data.categories : getBlocks(section);
  const limit = Number(section.bindings?.limit || 3);
  return (
    <section style={{ padding: section.style?.padding || '4rem 2rem', backgroundColor: section.style?.backgroundColor || '#ffffff' }}>
      <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '4rem', alignItems: 'center' }}>
        <div style={{ textAlign: 'left' }}>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 700, color: '#1a202c', marginBottom: '1.5rem', lineHeight: 1.2 }}>{section.content?.title}</h2>
          <p style={{ fontSize: '1.125rem', color: '#4a5568', lineHeight: 1.6, margin: 0 }}>{section.content?.subtitle}</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '2rem', alignItems: 'stretch' }}>
          {categories.slice(0, limit).map((category, index) => (
            <a
              key={category.primaryId || category.title || index}
              href={category.primaryId ? `/course?category=${category.primaryId}` : category.url || '/course'}
              style={{ backgroundColor: '#ffffff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,.05)', overflow: 'hidden', minHeight: 320, display: 'flex', flexDirection: 'column', color: 'inherit', textDecoration: 'none' }}
            >
              {getImageUrl(category) && <div style={{ height: 140, backgroundImage: `url(${getImageUrl(category)})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />}
              <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1a202c', margin: '0 0 .5rem' }}>{category.name || category.title}</h3>
                <div style={{ flex: 1 }}>
                  <RichTextDisplay
                    content={category.description || 'Master essential skills with expert-led courses'}
                    sx={{ fontSize: '.9rem', color: '#4a5568', lineHeight: 1.5 }}
                  />
                </div>
                <span style={{ color: educationAccent(section, theme), fontSize: '.9rem', fontWeight: 600 }}>Explore Courses -&gt;</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export const EducationFeatureGridSection = ({ section, theme }) => {
  const blocks = getBlocks(section);
  const accentColor = educationAccent(section, theme);
  const secondaryColor = educationSecondary(section, theme);
  return (
    <section className="py-20 px-4 bg-white relative" style={toStyle(section.style)}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            {section.content?.titlePrefix || 'Why'} <span style={{ color: secondaryColor }} className="font-extrabold">{section.content?.titleAccent || 'Choose'}</span> {section.content?.titleSuffix || 'Our Academy'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">{section.content?.subtitle}</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {blocks.map((item, index) => (
            <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100" key={`${item.title}-${index}`}>
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-md" style={{ color: educationBlockAccent(item, section, theme) }}>
                  <EducationIcon name={item.icon} fallback={index + 1} size={26} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{item.description}</p>
                  <a href={item.url || '#'} className="inline-flex items-center font-semibold" style={{ color: accentColor }}>Learn More -&gt;</a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const EducationFeaturedCoursesSection = ({ section, data, theme }) => {
  const navigate = useNavigate();
  const limit = Number(section.bindings?.limit || 4);
  const products = data.products.slice(0, limit);
  if (products.length) {
    return (
      <section style={{ backgroundColor: section.style?.backgroundColor || '#ffffff', padding: section.style?.padding || '4rem 0', textAlign: 'center' }}>
        <FeaturedCourses productList={products} navigate={navigate} loggedIn={data.isLoggedIn} />
      </section>
    );
  }
  return <EducationCourseCards section={section} courses={getBlocks(section)} theme={theme} />;
};

const EducationCourseCards = ({ section, courses, theme }) => (
  <section style={{ backgroundColor: section.style?.backgroundColor || '#ffffff', padding: section.style?.padding || '4rem 1.5rem', textAlign: 'center' }}>
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      {section.content?.title && <h2 style={{ fontSize: 36, margin: '0 0 12px', color: '#111827' }}>{section.content.title}</h2>}
      {section.content?.subtitle && <p style={{ color: '#64748b', margin: '0 auto 32px', maxWidth: 720 }}>{section.content.subtitle}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 24 }}>
        {(courses || []).map((course, index) => (
          <article key={`${course.title}-${index}`} style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', textAlign: 'left', background: '#fff', minHeight: 390, display: 'flex', flexDirection: 'column' }}>
            {course.imageUrl && (
              <div style={{ height: 220, background: '#ffffff', flex: '0 0 auto', overflow: 'hidden' }}>
                <img
                  src={course.imageUrl}
                  alt={course.title || 'Course'}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', padding: 10, boxSizing: 'border-box', display: 'block' }}
                />
              </div>
            )}
            <div style={{ padding: 20 }}>
              {course.level && <span style={{ display: 'inline-flex', padding: '4px 10px', borderRadius: 999, background: educationAccent(section, theme), color: '#fff', fontSize: 12, fontWeight: 700 }}>{course.level}</span>}
              <h3 style={{ margin: '14px 0 8px', color: '#111827', fontSize: 18, lineHeight: 1.3 }}>{course.title}</h3>
              <p style={{ color: '#64748b', lineHeight: 1.55, fontSize: 14 }}>{course.description}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const EducationMissionVisionSection = ({ section, theme }) => {
  const content = section.content || {};
  const values = getBlocks(section);
  const accentColor = educationAccent(section, theme);
  const secondaryColor = educationSecondary(section, theme);
  return (
    <section className="py-20 px-4 relative overflow-hidden" style={{ color: '#fff', backgroundImage: `linear-gradient(90deg, rgba(17,24,39,.98), rgba(17,24,39,.92)), url(${sanitizeStyleValue(section.style?.backgroundImage || '')})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">{content.titlePrefix} <span style={{ color: secondaryColor }}>{content.titleAccent}</span> {content.titleSuffix}</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">{content.subtitle}</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="text-white">
            {[{ title: content.missionTitle, description: content.missionDescription }, { title: content.visionTitle, description: content.visionDescription }].map((item, index) => (
              <div key={item.title} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20" style={{ marginTop: index ? 24 : 0 }}>
                <h3 className="text-2xl font-bold text-white mb-2">{item.title}</h3>
                <RichTextDisplay
                  content={item.description || ''}
                  sx={{ color: '#e5e7eb', fontSize: '1.125rem', lineHeight: 1.625 }}
                />
              </div>
            ))}
            <div className="mt-8 p-6 rounded-2xl text-center" style={{ background: `linear-gradient(90deg, ${secondaryColor}, ${accentColor})` }}>
              <p className="text-white italic text-lg mb-4">"{content.quote}"</p>
              <h3 className="font-bold text-white text-xl">{content.quoteAuthor}</h3>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">{content.valuesTitle || 'Our Core Values'}</h3>
            <div className="space-y-4">
              {values.map((item, index) => (
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl" key={`${item.title}-${index}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${educationBlockAccent(item, section, theme)}18`, color: educationBlockAccent(item, section, theme) }}>
                    <EducationIcon name={item.icon} fallback={index + 1} size={21} />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800">{item.title}</h5>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <a href={content.actionUrl || '/about'} className="mt-6 block w-full text-white font-semibold py-4 px-8 rounded-xl text-center" style={{ background: `linear-gradient(90deg, ${secondaryColor}, ${accentColor})` }}>
              {content.actionLabel || 'Learn More About Our Journey'}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export const EducationJoinCommunitySection = ({ section, theme }) => {
  const content = section.content || {};
  const accentColor = educationAccent(section, theme);
  const secondaryColor = educationSecondary(section, theme);
  const stats = Array.isArray(content.stats) ? content.stats : [];
  const imageUrl = getImageUrl(content);
  return (
    <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white" style={toStyle(section.style)}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-200">
              {imageUrl && <img loading="lazy" src={imageUrl} alt={content.imageAlt || ''} className="w-full h-auto rounded-xl shadow-md" />}
            </div>
            {content.badge && <div className="absolute -bottom-4 -left-4 text-white p-4 rounded-xl shadow-lg" style={{ background: accentColor }}>{content.badge}</div>}
          </div>
          <div className="space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800">{content.titlePrefix} <span style={{ color: secondaryColor }}>{content.titleAccent}</span> {content.titleSuffix}</h2>
            <p className="text-xl text-gray-600 leading-relaxed">{content.subtitle}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
              {getBlocks(section).map((item, index) => (
                <div className="flex items-center space-x-3" key={`${item.title}-${index}`}>
                  <div className="w-8 h-8 rounded-lg" style={{ background: `${educationBlockAccent(item, section, theme)}22` }} />
                  <span className="text-gray-700">{item.title}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              {(Array.isArray(content.actions) ? content.actions : []).map((action, index) => <EducationButton key={`${action.label}-${index}`} action={action} section={section} theme={theme} />)}
            </div>
            <div className="flex items-center gap-6 pt-6 border-t border-gray-200">
              {stats.map((item, index) => (
                <div className="text-center" key={`${item.value}-${index}`}>
                  <div className="text-2xl font-bold" style={{ color: index % 2 ? secondaryColor : accentColor }}>{item.value}</div>
                  <div className="text-gray-600 text-sm">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const EducationInfoPageSection = ({ section, theme }) => {
  const content = section.content || {};
  const accentColor = educationAccent(section, theme);
  const imageUrl = getImageUrl(content);
  return (
    <section style={{ ...toStyle(section.style), padding: section.style?.padding || '72px 24px', background: section.style?.backgroundColor || '#fff' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))', gap: 48, alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 48, lineHeight: 1.1, margin: '0 0 20px', color: '#111827' }}>{content.title}</h1>
          <p style={{ fontSize: 20, lineHeight: 1.7, color: '#4b5563', marginBottom: 28 }}>{content.subtitle}</p>
          <div style={{ display: 'grid', gap: 16 }}>
            {getBlocks(section).map((item, index) => (
              <article key={`${item.title}-${index}`} style={{ border: '1px solid #e2e8f0', borderRadius: 16, padding: 20, background: '#fff' }}>
                <h3 style={{ color: '#111827', marginTop: 0 }}>{item.title}</h3>
                <p style={{ color: '#64748b', lineHeight: 1.6 }}>{item.description}</p>
              </article>
            ))}
          </div>
          <ButtonLink label={content.actionLabel} url={content.actionUrl} accentColor={accentColor} radius={theme.buttonRadius} />
        </div>
        {imageUrl && <img src={imageUrl} alt={content.title || ''} style={{ width: '100%', borderRadius: 24, maxHeight: 520, objectFit: 'cover', boxShadow: '0 24px 70px rgba(15,23,42,.18)' }} />}
      </div>
    </section>
  );
};

const EducationSectionTitle = ({ section, theme, title, subtitle }) => {
  const accentColor = educationAccent(section, theme);
  return (
    <div style={{ maxWidth: 780, marginBottom: 28 }}>
      {section.content?.eyebrow && (
        <p style={{ margin: '0 0 10px', color: accentColor, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>
          {section.content.eyebrow}
        </p>
      )}
      {title && <h1 style={{ margin: 0, fontFamily: theme.headingFontFamily, color: theme.color || '#111827', fontSize: 'clamp(34px, 5vw, 54px)', lineHeight: 1.08 }}>{title}</h1>}
      {subtitle && <p style={{ margin: '14px 0 0', color: theme.mutedColor || '#64748b', fontSize: 18, lineHeight: 1.7 }}>{subtitle}</p>}
    </div>
  );
};

const EducationFunctionalPageSection = ({ section, theme, Component, defaultTitle, defaultSubtitle, maxWidth }) => {
  const content = section.content || {};
  const contentMaxWidth = maxWidth || section.style?.maxWidth || section.content?.maxWidth || theme.pageWidth || 1280;
  const isFullWidth = contentMaxWidth === '100%';
  return (
    <section style={{ ...toStyle(section.style), background: section.style?.backgroundColor || theme.background || '#f8fafc', padding: section.style?.padding || '56px 0', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <div style={{ width: '100%', maxWidth: isFullWidth ? '100%' : contentMaxWidth, margin: isFullWidth ? '0' : '0 auto', boxSizing: 'border-box' }}>
        <EducationSectionTitle section={section} theme={theme} title={content.title || defaultTitle} subtitle={content.subtitle || defaultSubtitle} />
        <div
          style={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            borderRadius: isFullWidth ? 0 : (theme.cardRadius || 16),
            background: theme.surface || '#ffffff',
            border: isFullWidth ? 'none' : `1px solid ${theme.borderColor || '#e5e7eb'}`,
            boxShadow: isFullWidth ? 'none' : (theme.cardShadow || '0 10px 30px rgba(15,23,42,.08)'),
            overflow: 'hidden'
          }}
        >
          <Component />
        </div>
      </div>
    </section>
  );
};

export const EducationProductsPageSection = ({ section, theme }) => {
  // The course list (`Products`) is a full-page component that owns its own
  // layout, container width, and background. Render it standalone/full-width
  // instead of boxing it inside the constrained card wrapper + title block.
  void theme;
  const background = section?.style?.backgroundColor;
  return (
    <section
      style={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        paddingTop: 0,
        ...(background ? { background } : {})
      }}
    >
      <Products />
    </section>
  );
};

export const EducationCourseDetailPageSection = ({ section, theme }) => {
  void theme;
  const background = section?.style?.backgroundColor;
  return (
    <section
      style={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        ...(background ? { background } : {})
      }}
    >
      <CourseInformation
        templateContent={{ ...(section.content || {}), maxWidth: '100%' }}
        templateTheme={theme}
      />
    </section>
  );
};

export const EducationCartPageSection = ({ section, theme }) => (
  <EducationFunctionalPageSection
    section={section}
    theme={theme}
    Component={Cart}
    defaultTitle="Cart"
    defaultSubtitle="Review selected courses before checkout."
  />
);

export const EducationSummaryPageSection = ({ section, theme }) => (
  <EducationFunctionalPageSection
    section={section}
    theme={theme}
    Component={OrderHistory}
    defaultTitle="Order History"
    defaultSubtitle="Track enrollments, orders, and payment status."
  />
);

export const EducationAboutPageSection = ({ section, theme }) => {
  const c = section.content || {};
  const accentColor = educationAccent(section, theme);
  const secondaryColor = educationSecondary(section, theme);
  const imageUrl = c.heroImageContent?.displayLink || c.heroImageContent?.storedLink || c.heroImageUrl || getImageUrl(c) || educationReaderAssets.aboutHero;
  const signupImage = c.signupImageContent?.displayLink || c.signupImageContent?.storedLink || c.signupImageUrl || educationReaderAssets.aboutSignup;
  const cards = getBlocks(section).length ? getBlocks(section) : defaultEducationAboutBlocks;
  const padding = !section.style?.padding || section.style.padding === '0' ? '96px 0 0' : section.style.padding;
  return (
    <section id="about-page" style={{ ...toStyle(section.style), background: section.style?.backgroundColor || '#ffffff', padding }}>
      <div id="tech-section" style={{ background: '#ffffff' }}>
        <div className="education-about-hero" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, padding: '32px', maxWidth: theme.pageWidth || 1280, margin: '0 auto' }}>
          <div style={{ maxWidth: 320, flex: '1 1 0' }}>
            <img loading="lazy" src={imageUrl} alt={c.title || 'Welcome'} id="hero-image" style={{ width: '100%', height: 'auto', display: 'block' }} />
          </div>
          <div style={{ maxWidth: 560, flex: '2 1 0' }}>
            <h1 id="hero-title" style={{ margin: '0 0 16px', color: secondaryColor, fontSize: 'clamp(24px, 3vw, 32px)', fontWeight: 800, lineHeight: 1.2 }}>
              {c.title || 'AVPS Academy'}
            </h1>
            <p id="hero-subtitle" style={{ margin: 0, color: '#374151', fontSize: 'clamp(18px, 2vw, 20px)', lineHeight: 1.7 }}>
              {c.subtitle || 'At the AVPS Academy, we are committed to providing unparalleled educational experiences for our students. Our esteemed faculty members are experts in their respective fields and are dedicated to helping students achieve their academic and personal goals.'}
            </p>
          </div>
        </div>
      </div>

      {c.showWhyChoose !== false && (
        <div id="about-section" style={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #e5e7eb, #f3f4f6)', padding: '64px 32px', textAlign: 'center', color: '#1f2937' }}>
          <div style={{ position: 'absolute', inset: 0, background: '#ffffff', opacity: 0.6, zIndex: 1 }} />
          <div style={{ position: 'relative', zIndex: 2, maxWidth: theme.pageWidth || 1280, margin: '0 auto' }}>
            <h2 id="about-title" style={{ margin: '0 0 24px', textAlign: 'left', color: secondaryColor, fontSize: 'clamp(32px, 4vw, 40px)', fontWeight: 800, lineHeight: 1.2 }}>
              {c.aboutSectionTitle || 'Why Choose the AVPS Academy?'}
            </h2>
            <div id="about-cards-wrapper" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24 }}>
              {cards.map((item, index) => (
                <article key={`${item.title}-${index}`} id={`about-card-${index + 1}`} style={{ width: '100%', maxWidth: 320, borderRadius: 8, background: '#ffffff', padding: 16, textAlign: 'center', boxShadow: '0 10px 20px rgba(15, 23, 42, 0.16)', transition: 'transform .3s ease, box-shadow .3s ease' }}>
                  <div style={{ marginBottom: 16, display: 'flex', height: 96, alignItems: 'center', justifyContent: 'center' }}>
                    {getImageUrl(item) && <img loading="lazy" src={getImageUrl(item)} alt={item.title} style={{ maxWidth: '100%', maxHeight: '100%' }} />}
                  </div>
                  <h3 style={{ margin: '0 0 8px', color: educationBlockAccent(item, section, theme) || accentColor, fontSize: 18, fontWeight: 700 }}>{item.title}</h3>
                  <p style={{ margin: 0, color: '#4b5563', fontSize: 14, lineHeight: 1.55 }}>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      {c.showSignupBanner !== false && (
        <div id="about-signup-banner" style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', width: '100%' }}>
          <img loading="lazy" src={signupImage} alt="Welcome" id="signup-image" style={{ width: '100%', height: 'auto', display: 'block' }} />
          <a href={c.signupButtonUrl || '/sign-in'} id="signup-button" style={{ position: 'absolute', left: '50%', top: '38%', zIndex: 10, transform: 'translateX(-50%)', whiteSpace: 'nowrap', borderRadius: 8, padding: '12px 24px', background: secondaryColor, color: '#ffffff', textDecoration: 'none', fontSize: 18, fontWeight: 700 }}>
            {c.signupButtonLabel || 'Sign Up'}
          </a>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .education-about-hero {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
        }
      `}</style>
    </section>
  );
};

export const EducationContactPageSection = ({ section, theme }) => {
  const content = section.content || {};
  const contactInfo = useTemplateContactInfo(content);
  const c = { ...content, phoneValue: contactInfo.phoneValue, emailValue: contactInfo.emailValue, addressValue: contactInfo.addressValue };
  const fields = getBlocks({ content: { blocks: c.fields || defaultEducationContactFields } });
  const inquiryForm = useTemplateInquiryForm(fields);
  const secondaryColor = educationSecondary(section, theme);
  const imageUrl = getImageUrl(c) || educationReaderAssets.contact;
  const padding = !section.style?.padding || section.style.padding === '0' ? '96px 24px 64px' : section.style.padding;

  return (
    <section style={{ ...toStyle(section.style), background: section.style?.backgroundColor || '#ffffff', padding, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1152, margin: '0 auto' }}>
        <div style={{ marginBottom: 64, textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 20px', color: '#2D3748', fontSize: 'clamp(40px, 5vw, 48px)', fontWeight: 800, lineHeight: 1.15 }}>
            {c.title || 'Get in'} <span style={{ color: secondaryColor }}>{c.titleAccent || 'touch'}</span>
          </h1>
          <p style={{ maxWidth: 672, margin: '0 auto', color: '#718096', fontSize: 18, lineHeight: 1.7 }}>
            {c.subtitle || "Have questions about admissions or courses? We're here to guide you."}
          </p>
        </div>

        <div className="education-contact-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 32 }}>
          <div style={{ position: 'relative', minHeight: 500, overflow: 'hidden', borderRadius: 24, boxShadow: '0 25px 50px rgba(15, 23, 42, 0.25)' }}>
            <img src={imageUrl} alt="Education contact" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,.65), rgba(0,0,0,.2), transparent)' }} />
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 32, color: '#ffffff' }}>
              <h2 style={{ margin: '0 0 12px', fontSize: 30, fontWeight: 800 }}>{c.imageHeading || "Let's Connect"}</h2>
              <p style={{ margin: 0, color: 'rgba(255,255,255,.9)', fontSize: 18 }}>{c.imageSubtitle || 'Building relationships that matter'}</p>
            </div>
          </div>

          <form onSubmit={inquiryForm.submitInquiry} style={{ borderRadius: 24, background: '#ffffff', padding: 32, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 32px', textAlign: 'center', color: '#111827', fontSize: 'clamp(24px, 3vw, 30px)', fontWeight: 800 }}>{c.formTitle || 'Send us a Message'}</h2>
            <div className="education-contact-field-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 24 }}>
              {fields.filter((field) => field.type !== 'textarea').map((field) => {
                const fieldName = inquiryForm.fieldNameFromLabel(field.label, field.type);
                const hasError = Boolean(inquiryForm.errors[fieldName]);
                return (
                  <label key={field.label} style={{ display: 'block' }}>
                    <span style={{ display: 'block', marginBottom: 8, color: '#2D3748', fontWeight: 600 }}>{field.label}</span>
                    <input
                      placeholder={`Enter ${String(field.label || '').toLowerCase()}`}
                      type={field.type || 'text'}
                      value={inquiryForm.values[fieldName] || ''}
                      onChange={(event) => inquiryForm.setFieldValue(fieldName, event.target.value)}
                      style={{ width: '100%', border: `1px solid ${hasError ? '#dc2626' : '#e2e8f0'}`, borderRadius: 8, background: '#ffffff', padding: '16px', color: '#111827', outline: 'none', boxSizing: 'border-box' }}
                    />
                    {hasError && <p style={{ margin: '6px 0 0', color: '#dc2626', fontSize: 12 }}>{inquiryForm.errors[fieldName]}</p>}
                  </label>
                );
              })}
            </div>
            {fields.filter((field) => field.type === 'textarea').map((field) => {
              const fieldName = inquiryForm.fieldNameFromLabel(field.label, field.type);
              const hasError = Boolean(inquiryForm.errors[fieldName]);
              return (
                <label key={field.label} style={{ display: 'block', marginTop: 24 }}>
                  <span style={{ display: 'block', marginBottom: 8, color: '#2D3748', fontWeight: 600 }}>{field.label}</span>
                  <textarea
                    placeholder="Tell us how we can help..."
                    rows={5}
                    value={inquiryForm.values[fieldName] || ''}
                    onChange={(event) => inquiryForm.setFieldValue(fieldName, event.target.value)}
                    style={{ width: '100%', minHeight: 150, border: `1px solid ${hasError ? '#dc2626' : '#e2e8f0'}`, borderRadius: 8, background: '#ffffff', padding: '16px', color: '#111827', outline: 'none', boxSizing: 'border-box', resize: 'vertical' }}
                  />
                  {hasError && <p style={{ margin: '6px 0 0', color: '#dc2626', fontSize: 12 }}>{inquiryForm.errors[fieldName]}</p>}
                </label>
              );
            })}
            {inquiryForm.status && <p style={{ color: inquiryForm.status.type === 'success' ? '#166534' : '#b91c1c', fontWeight: 700 }}>{inquiryForm.status.message}</p>}
            <button type="submit" disabled={inquiryForm.submitting} style={{ width: '100%', border: 0, borderRadius: 12, background: secondaryColor, color: '#ffffff', padding: '16px', fontWeight: 800, marginTop: 32, boxShadow: '0 10px 20px rgba(15, 23, 42, 0.14)', opacity: inquiryForm.submitting ? .72 : 1 }}>
              {inquiryForm.submitting ? 'Sending...' : c.submitLabel || 'Send Message'}
            </button>
          </form>
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .education-contact-grid,
          .education-contact-field-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
      `}</style>
    </section>
  );
};

export const EducationAuthEntrySection = ({ section, theme }) => {
  const isSignup = /sign.?up|create/i.test(section.content?.title || section.content?.actionLabel || '');
  const Component = isSignup ? Signup : Login;
  return (
    <EducationFunctionalPageSection
      section={section}
      theme={theme}
      Component={Component}
      defaultTitle={isSignup ? 'Create your account' : 'Sign in to your account'}
      defaultSubtitle={section.content?.subtitle || 'Access your courses and learning activity.'}
    />
  );
};


